/**
 * Official Saudi riyal symbol — vector drawn on canvas (NOT a font glyph).
 *
 * Source: Saudi Central Bank (SAMA), "Saudi riyal symbol" page
 *   https://www.sama.gov.sa/en-US/Currency/SRS/Pages/Guideline.aspx
 * file  https://www.sama.gov.sa/ar-sa/Currency/Documents/Saudi_Riyal_Symbol-2.svg   (sha256 dc035ad6d18c543a79d7e2f24458ab17b869c030875dc2f1d18f2fa9dda9e404)
 * Geometry: viewBox 1124.14 x 1256.39 (SAMA Guidelines.pdf p.14: width X.425, height X.475).
 *
 * SAMA usage rules (Guidelines.pdf p.17, "Usage Rules") followed here:
 *   Position  - to the LEFT of the numeral (also left of a written value / of the minus sign)
 *   Alignment - the symbol's height is aligned with the height of the text
 *   Spacing   - a space between the symbol and the numeral
 *   Direction - the symbol's direction matches the text direction
 *   Negative space - clear space equal to ONE THIRD of the symbol's height around it
 *   Contrast, proportions and geometry are kept (uniform scale only, never stretched)
 * Font Designers' Guidelines p.8-9 (vertical alignment): the top of the Lam aligns with the top of the Latin numerals and the
 * Lam sits ON THE BASELINE; the Yaa' dots fall below the baseline. Measured on SAMA's own figure (p.9, 200 dpi): digit height 358 px,
 * symbol 381 px, shared top -> the baseline is at 358/381 = 0.9396 of the symbol height = 1180.5 viewBox units.
 */
export const RIYAL_VIEW_W = 1124.14;
export const RIYAL_VIEW_H = 1256.39;
/** y (viewBox units) of the baseline: the Lam sits here, the Yaa' dots hang below. */
export const RIYAL_BASELINE = 1180.5;
export const RIYAL_PATHS = [
  "M699.62,1113.02h0c-20.06,44.48-33.32,92.75-38.4,143.37l424.51-90.24c20.06-44.47,33.31-92.75,38.4-143.37l-424.51,90.24Z",
  "M1085.73,895.8c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.33v-135.2l292.27-62.11c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.27V66.13c-50.67,28.45-95.67,66.32-132.25,110.99v403.35l-132.25,28.11V0c-50.67,28.44-95.67,66.32-132.25,110.99v525.69l-295.91,62.88c-20.06,44.47-33.33,92.75-38.42,143.37l334.33-71.05v170.26l-358.3,76.14c-20.06,44.47-33.32,92.75-38.4,143.37l375.04-79.7c30.53-6.35,56.77-24.4,73.83-49.24l68.78-101.97v-.02c7.14-10.55,11.3-23.27,11.3-36.97v-149.98l132.25-28.11v270.4l424.53-90.28Z",
];

const NUM = /-?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/gi;

/** Parses the SVG path subset the official file uses (M m L l H h V v C c Z z) into absolute commands. */
export function parsePath(d) {
  const out = [];
  const re = /([MmLlHhVvCcZz])([^MmLlHhVvCcZz]*)/g;
  let cx = 0; let cy = 0; let sx = 0; let sy = 0; let m;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(d))) {
    const cmd = m[1]; const a = (m[2].match(NUM) || []).map(Number); let i = 0;
    const rel = cmd === cmd.toLowerCase();
    const C = cmd.toUpperCase();
    if (C === 'Z') { out.push(['Z']); cx = sx; cy = sy; continue; }
    const need = { M: 2, L: 2, H: 1, V: 1, C: 6 }[C];
    let first = true;
    while (i < a.length) {
      if (C === 'M') {
        const x = a[i] + (rel ? cx : 0); const y = a[i + 1] + (rel ? cy : 0);
        out.push([first ? 'M' : 'L', x, y]); cx = x; cy = y; if (first) { sx = x; sy = y; }
      } else if (C === 'L') { const x = a[i] + (rel ? cx : 0); const y = a[i + 1] + (rel ? cy : 0); out.push(['L', x, y]); cx = x; cy = y; }
      else if (C === 'H') { const x = a[i] + (rel ? cx : 0); out.push(['L', x, cy]); cx = x; }
      else if (C === 'V') { const y = a[i] + (rel ? cy : 0); out.push(['L', cx, y]); cy = y; }
      else if (C === 'C') {
        const p = [0, 1, 2, 3, 4, 5].map((k) => a[i + k] + (rel ? (k % 2 === 0 ? cx : cy) : 0));
        out.push(['C', ...p]); cx = p[4]; cy = p[5];
      }
      i += need; first = false;
    }
  }
  return out;
}

let parsed = null;

/**
 * Draws the symbol. `x` = left edge, `baselineY` = the text baseline, `digitH` = height of the numerals it sits beside
 * (the Lam's top lands on the numerals' top). Fill = `color` (use the price colour).
 * @returns {{w:number,h:number,gap:number}} width, full height (incl. the descending dots) and the SAMA clear space (h/3)
 */
export function drawRiyal(ctx, x, baselineY, digitH, color) {
  if (!parsed) parsed = RIYAL_PATHS.map(parsePath);
  const k = digitH / RIYAL_BASELINE;
  const top = baselineY - digitH;
  ctx.save();
  ctx.fillStyle = color;
  parsed.forEach((cmds) => {
    ctx.beginPath();
    cmds.forEach(([c, ...v]) => {
      if (c === 'M') ctx.moveTo(x + v[0] * k, top + v[1] * k);
      else if (c === 'L') ctx.lineTo(x + v[0] * k, top + v[1] * k);
      else if (c === 'C') ctx.bezierCurveTo(x + v[0] * k, top + v[1] * k, x + v[2] * k, top + v[3] * k, x + v[4] * k, top + v[5] * k);
      else ctx.closePath();
    });
    ctx.fill();
  });
  ctx.restore();
  return { w: RIYAL_VIEW_W * k, h: RIYAL_VIEW_H * k, gap: (RIYAL_VIEW_H * k) / 3 };
}

/** Size of the symbol for numerals `digitH` tall (no drawing). */
export const riyalSize = (digitH) => {
  const k = digitH / RIYAL_BASELINE;
  return { w: RIYAL_VIEW_W * k, h: RIYAL_VIEW_H * k, gap: (RIYAL_VIEW_H * k) / 3 };
};
